import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, FeatureFlagGroup } from '../../lib/api';
import { useToastStore } from '../../stores/toast';

interface GroupListProps {
  groups: FeatureFlagGroup[];
  onSelect: (id: string) => void;
}

export default function GroupList({ groups, onSelect }: GroupListProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [confirm, confirmDialog] = useConfirmDialog();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteFeatureFlagGroup(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flag-groups'] });
        showToast('그룹이 삭제되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('그룹 삭제 중 오류가 발생했습니다.', 'error');
    },
  });

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        등록된 사용자 그룹이 없습니다.
      </div>
    );
  }

  return (
    <Card padding="none" radius="lg" elevation="none" className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              그룹 이름
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              설명
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
              멤버 수
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
              연결 플래그
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              생성일
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
              관리
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {groups.map((g) => (
            <tr
              key={g.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelect(g.id)}
            >
              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                {g.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                {g.description || '-'}
              </td>
              {/* 멤버 수=blue / 플래그 연결 수=green — 두 수치를 색으로 가르는 범례라 blue 유지. */}
              <td className="px-4 py-3 text-sm text-center text-gray-700">
                <span className="inline-flex items-center justify-center bg-blue-50 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  {g.memberCount}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-center text-gray-700">
                <span className="inline-flex items-center justify-center bg-green-50 text-green-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
                  {g.flagLinkCount}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {new Date(g.createdAt).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul' })}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      await confirm({
                        title: '그룹 삭제',
                        body: `"${g.name}" 그룹을 삭제하시겠습니까?\n연결된 오버라이드도 모두 삭제됩니다.`,
                        confirmLabel: '삭제',
                        danger: true,
                      })
                    ) {
                      deleteMutation.mutate(g.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* 표 밖에 둔다 — 행 안에 두면 확인 창 클릭이 React 트리를 따라 행의 onClick(상세 열기)로 번진다. */}
      {confirmDialog}
    </Card>
  );
}
